import moment from 'moment'
import { DATE_FORMAT } from '@/constants'

export default {
  required(v) {
    return (v != null && v !== '') || 'Field is required'
  },
  name(v) {
    return (
      !v ||
      /^[a-zA-Z ()'-]+$/.test(v) ||
      "Can only contain letters, spaces, and - ( ) '"
    )
  },
  phone(val) {
    return (
      !val ||
      /^[0-9 ()\\-]+$/.test(val) ||
      `Can only contain 0-9, spaces and - ( ) \\`
    )
  },
  email(v) {
    return !v || /\S+@\S+\.\S+/.test(v) || 'Invalid email'
  },
  noFutureDate(date) {
    if (
      date &&
      moment(date, ['MM/YYYY', DATE_FORMAT, 'DD-MM-YYYY', 'DD MMM YYYY']) >
        moment()
    ) {
      return 'Cannot be a future date'
    }
    return true
  },
  noFutureMonthAndYear(date) {
    if (
      date &&
      moment(date, ['MM-YYYY', 'YYYY-MM']).format('YYYY-MM') >
        moment().format('YYYY-MM')
    ) {
      return 'Cannot be a future date'
    }
    return true
  },
  noMonthAndYearBeforeJan2004(date) {
    if (
      date &&
      moment(date, ['MM-YYYY', 'YYYY-MM']).format('YYYY-MM') <
        moment(new Date('2004-01-01').toISOString().slice(0, 7)).format(
          'YYYY-MM'
        )
    ) {
      return 'Cannot be earlier than 2004'
    }
    return true
  },
  noPastDate(date) {
    if (date) {
      const mDate = moment(date, [
        'MM/YYYY',
        DATE_FORMAT,
        'DD-MM-YYYY',
        'DD MMM YYYY'
      ])
      if (
        mDate.isBefore(
          moment(),
          mDate.creationData().format === 'MM/YYYY' ? 'month' : 'day'
        )
      ) {
        return 'Cannot be a past date'
      }
    }
    return true
  },
  getMaxFutureYearValidator(yearsInTheFuture) {
    // returns a validator function
    return date => {
      if (date) {
        const maxYear = new Date().getFullYear() + yearsInTheFuture
        const mDate = moment(date, [
          'MM/YYYY',
          DATE_FORMAT,
          'DD-MM-YYYY',
          'DD MMM YYYY'
        ])
        return mDate.year() <= maxYear || `Cannot be a later than ${maxYear}`
      }
      return true
    }
  },
  dateOrder(fromDate, toDate) {
    return (
      fromDate &&
      toDate &&
      moment(fromDate, ['MM-YYYY', DATE_FORMAT]) <=
        moment(toDate, ['MM-YYYY', DATE_FORMAT])
    )
  },
  getPrimaryStudentMinAgeValidator(enrolmentYear) {
    const minAge = 5
    return value => {
      const newIntakeYearStartDate = moment(`${enrolmentYear}-07-31`, [
        'YYYY-MM-DD'
      ])

      const mDate = moment(value, ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD MMM YYYY'])
      const studentAgeAtStartDate = newIntakeYearStartDate.diff(
        mDate,
        'year',
        true
      )
      return (
        studentAgeAtStartDate >= minAge ||
        `This student is too young to begin school in ${enrolmentYear}.`
      )
    }
  },
  primaryStudentMaxAge(date) {
    const cutOffDate = moment('01-01-2000', [
      'YYYY-MM-DD',
      'DD-MM-YYYY',
      'DD MMM YYYY'
    ])
    return (
      cutOffDate.diff(
        moment(date, ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD MMM YYYY'])
      ) < 0 ||
      'Please ensure the date is no earlier than 2000 and no later than one week ago.'
    )
  },
  getSecondaryStudentMinAgeValidator(enrolmentYear) {
    // student must not be younger than 9
    const minAge = 9
    const thisYear = new Date().getFullYear()

    return value => {
      const cutOffDate =
        enrolmentYear === thisYear
          ? moment().startOf('day')
          : moment(`${enrolmentYear}-01-01`, ['YYYY-MM-DD'])
      const mDate = moment(value, ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD MMM YYYY'])

      const studentAgeAtStartOfCalendarYear = cutOffDate.diff(
        mDate,
        'year',
        true
      )
      return (
        studentAgeAtStartOfCalendarYear >= minAge ||
        `This student is too young to begin high school in ${enrolmentYear}.`
      )
    }
  },
  getSecondaryStudentMaxAgeValidator(enrolmentYear) {
    // Student must not be older than 17
    // note: 17 is ok, 17 and 1 day is not ok
    const maxAge = 17
    const thisYear = new Date().getFullYear()

    return value => {
      const cutOffDate =
        enrolmentYear === thisYear
          ? moment().startOf('day')
          : moment(`${enrolmentYear}-01-01`, ['YYYY-MM-DD'])
      const mDate = moment(value, ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD MMM YYYY'])

      const studentAgeAtStartOfCalendarYear = cutOffDate.diff(
        mDate,
        'year',
        true
      )
      return (
        studentAgeAtStartOfCalendarYear < maxAge ||
        `This student must be no younger than 9 and no older than 17 years of age to enrol in high school in ${enrolmentYear}.`
      )
    }
  },

  getY67TStudentMinAgeValidator(birthDate) {
    const dateSeventeenYearsAgo = new Date()
    dateSeventeenYearsAgo.setFullYear(dateSeventeenYearsAgo.getFullYear() - 17)
    const seventeenYearsAgo = dateSeventeenYearsAgo.toISOString().slice(0, 10)

    const dateObject = moment(birthDate, [
      'YYYY-MM-DD',
      'DD-MM-YYYY',
      'DD MMM YYYY'
    ])
    const minBirthDate = moment(seventeenYearsAgo)
    return (
      dateObject.diff(minBirthDate) >= 0 ||
      'Children enrolling in high school must be no younger than 9 and no older than 17 years of age. Please choose a date of birth within this range.'
    )
  },

  getY67TStudentMaxAgeValidator(birthDate) {
    const dateNineYearsAgo = new Date()
    dateNineYearsAgo.setFullYear(dateNineYearsAgo.getFullYear() - 9)
    const nineYearsAgo = dateNineYearsAgo.toISOString().slice(0, 10)

    const dateObject = moment(birthDate, [
      'YYYY-MM-DD',
      'DD-MM-YYYY',
      'DD MMM YYYY'
    ])
    const nineYearsAgoDate = moment(nineYearsAgo)
    return (
      dateObject.diff(nineYearsAgoDate) <= 0 ||
      'Children enrolling in high school must be at least 9 years of age.'
    )
  }
}
